if (!customElements.get('bundle-component')) {
  class BundleComponent extends HTMLElement {
    constructor() {
      super();

      this.items = [];
      this.total = 0;
      this.submitButton = null;
      this.totalElement = null;
      // Bundle discount only applies when more than one product is selected
      this.discountActive = false;

      this.includeItem = this.includeItem.bind(this);
      this.updateTotal = this.updateTotal.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      const runInit = () => {
        this.getElements();
        this.updateTotal();
        this.initAutoPick();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runInit);
      } else {
        setTimeout(runInit, 0);
      }
    }

    disconnectedCallback() {
      // Cleanup listeners if component is removed
    }

    getElements() {
      this.items = Array.from(this.querySelectorAll('.bundle-item, .fbt-item'));
      this.submitButton = this.querySelector('#add-to-bag, #fbt-add-to-cart, .add-to-bag-btn, .fbt-add-to-cart-btn');
      this.totalElement = this.querySelector('#total-price, #fbt-total-price, .total-amount, .fbt-total-amount');

      this.setEvents();
    }

    setEvents() {
      this.items.forEach(bundleItemNode => {
        // Skip items whose listeners are already bound (setEvents re-runs after auto-pick injection)
        if (bundleItemNode.dataset.stlBound === '1') return;
        bundleItemNode.dataset.stlBound = '1';

        // Toggle item check on checkbox click or image wrapper click
        const checkbox = bundleItemNode.querySelector('[data-add-item], [data-fbt-checkbox], .custom-checkbox, .fbt-checkbox');
        const imageWrapper = bundleItemNode.querySelector('.item-image-wrapper, .fbt-image-wrapper, [data-toggle-fbt-item]');
        const variantSelect = bundleItemNode.querySelector('.variant-selector, .fbt-variant-select');
        const discountCheckbox = bundleItemNode.querySelector('input.discount-checkbox');

        if (checkbox) {
          checkbox.addEventListener('click', (e) => {
            e.stopPropagation();
            this.includeItem(bundleItemNode);
          });
        }

        if (imageWrapper) {
          imageWrapper.addEventListener('click', (e) => {
            // Prevent double firing if clicking directly on checkbox
            if (e.target.closest('[data-add-item], [data-fbt-checkbox], .custom-checkbox, .fbt-checkbox')) return;
            this.includeItem(bundleItemNode);
          });
        }

        if (variantSelect) {
          variantSelect.addEventListener('change', (e) => this.handleVariantChange(bundleItemNode, e.target));
        }

        if (discountCheckbox) {
          discountCheckbox.addEventListener('change', () => {
            this.updateItemPriceDisplay(bundleItemNode);
            this.updateTotal();
          });
        }

        // Swatch Image Switcher
        const swatches = bundleItemNode.querySelectorAll('.grid-product__swatches a, .fbt-color-swatches a, .fbt-swatch-dot');
        swatches.forEach(swatch => {
          swatch.addEventListener('click', (e) => {
            const mainImgUrl = swatch.getAttribute('data-main-image');
            if (mainImgUrl) {
              e.preventDefault();
              const mainImg = bundleItemNode.querySelector('.item-image, .fbt-image');
              if (mainImg) {
                mainImg.src = mainImgUrl;
              }
              swatches.forEach(s => {
                const label = s.querySelector('.swatch-label--color');
                if (label) label.classList.remove('active');
                s.classList.remove('is-selected', 'active');
              });
              const currentLabel = swatch.querySelector('.swatch-label--color');
              if (currentLabel) currentLabel.classList.add('active');
              swatch.classList.add('is-selected', 'active');
            }
          });
        });
      });

      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.handleSubmit);
        this.submitButton.addEventListener('click', this.handleSubmit);
      }
    }

    includeItem(bundleItemNode) {
      const checkbox = bundleItemNode.querySelector('.custom-checkbox, .fbt-checkbox');
      const isChecked = bundleItemNode.classList.toggle('checked');

      if (checkbox) {
        checkbox.classList.toggle('checked', isChecked);
      }

      this.updateTotal();
    }

    getItemPrice(bundleItemNode) {
      const variantSelect = bundleItemNode.querySelector('.variant-selector, .fbt-variant-select');
      let basePrice = 0;
      let isValid = true;

      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];

        if (selectedOption && selectedOption.value && !selectedOption.disabled) {
          const rawPrice = parseInt(selectedOption.getAttribute('data-price'), 10);
          if (!isNaN(rawPrice)) basePrice = rawPrice;
        } else {
          // Sold-out variant or a combination that doesn't exist —
          // keep the default price for display but block submission
          isValid = false;
          const defaultPrice = parseInt(bundleItemNode.getAttribute('data-default-price'), 10);
          if (!isNaN(defaultPrice)) basePrice = defaultPrice;
        }
      } else {
        const defaultPrice = parseInt(bundleItemNode.getAttribute('data-default-price'), 10);
        if (!isNaN(defaultPrice)) basePrice = defaultPrice;
      }

      const discountCheckbox = bundleItemNode.querySelector('input.discount-checkbox');
      let effectivePrice = basePrice;
      let isDiscountApplied = false;

      if (discountCheckbox && discountCheckbox.checked && basePrice > 0) {
        const applied = this.applyDiscount(
          basePrice,
          discountCheckbox.getAttribute('data-discount-type'),
          parseFloat(discountCheckbox.getAttribute('data-discount-value'))
        );
        effectivePrice = applied.price;
        isDiscountApplied = applied.isApplied;
      } else if (basePrice > 0 && this.discountActive) {
        // Component-level discount configured via section settings
        const applied = this.applyDiscount(
          basePrice,
          this.getAttribute('data-discount-type'),
          parseFloat(this.getAttribute('data-discount-value'))
        );
        effectivePrice = applied.price;
        isDiscountApplied = applied.isApplied;
      }

      return {
        basePrice,
        effectivePrice,
        isDiscountApplied,
        isValid
      };
    }

    applyDiscount(basePrice, dType, dVal) {
      if (isNaN(dVal) || dVal <= 0) {
        return { price: basePrice, isApplied: false };
      }

      // Percentage discount (type === "true" or true)
      if (dType === 'true' || dType === 'percentage' || dType === '1') {
        const discountAmount = Math.round(basePrice * (dVal / 100));
        return { price: Math.max(0, basePrice - discountAmount), isApplied: true };
      }

      // Fixed amount discount in cents
      if (dType === 'false' || dType === 'fixed_amount' || dType === '0') {
        return { price: Math.max(0, basePrice - dVal), isApplied: true };
      }

      return { price: basePrice, isApplied: false };
    }

    updateItemPriceDisplay(bundleItemNode) {
      const { basePrice, effectivePrice, isDiscountApplied, isValid } = this.getItemPrice(bundleItemNode);
      bundleItemNode.classList.toggle('combo-unavailable', !isValid);
      const priceSpan = bundleItemNode.querySelector('.current-price, .fbt-current-price');
      const oldPriceSpan = bundleItemNode.querySelector('.old-price, .fbt-compare-price');

      if (priceSpan) {
        priceSpan.textContent = this.formatMoney(effectivePrice);
      }

      if (oldPriceSpan) {
        if (isDiscountApplied) {
          // Strike out original base price prior to discount
          oldPriceSpan.textContent = this.formatMoney(basePrice);
          oldPriceSpan.style.display = 'inline';
        } else {
          // Revert to compare_at_price if present and higher than basePrice, or hide
          const initialComparePrice = parseInt(bundleItemNode.getAttribute('data-compare-price'), 10);
          if (!isNaN(initialComparePrice) && initialComparePrice > basePrice) {
            oldPriceSpan.textContent = this.formatMoney(initialComparePrice);
            oldPriceSpan.style.display = 'inline';
          } else {
            oldPriceSpan.style.display = 'none';
          }
        }
      }
    }

    handleVariantChange(bundleItemNode, selectElement) {
      this.updateItemPriceDisplay(bundleItemNode);
      this.updateTotal();
      this.updatePreorderBadge(bundleItemNode, selectElement);
    }

    updatePreorderBadge(bundleItemNode, selectElement) {
      if (!selectElement) return;
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      const isPreorder = selectedOption && selectedOption.getAttribute('data-preorder') === 'true';
      const imgWrapper = bundleItemNode.querySelector('.fbt-image-wrapper, .item-image-wrapper, .product_card__image-wrapper');
      if (!imgWrapper) return;

      let labelsContainer = imgWrapper.querySelector('.labels');
      let preorderBadge = imgWrapper.querySelector('.label.preorder');

      if (isPreorder) {
        if (!labelsContainer) {
          labelsContainer = document.createElement('div');
          labelsContainer.className = 'labels';
          imgWrapper.appendChild(labelsContainer);
        }
        if (!preorderBadge) {
          preorderBadge = document.createElement('span');
          preorderBadge.className = 'label preorder';
          const preorderText = (window.StyleHatch && window.StyleHatch.Strings && window.StyleHatch.Strings.preOrder) ? window.StyleHatch.Strings.preOrder : 'Pre-Order';
          preorderBadge.textContent = preorderText;
          labelsContainer.appendChild(preorderBadge);
        } else {
          preorderBadge.style.display = '';
        }
      } else if (preorderBadge) {
        preorderBadge.style.display = 'none';
      }
    }

    formatMoney(valueInCents) {
      const shopify = window.Shopify;
      let locale = 'en-US';
      let currencyCode = 'USD';

      if (shopify) {
        if (shopify.locale && shopify.country) {
          locale = `${shopify.locale}-${shopify.country}`;
        } else if (shopify.locale) {
          locale = shopify.locale;
        }

        if (shopify.currency && shopify.currency.active) {
          currencyCode = shopify.currency.active;
        }
      }

      const amount = valueInCents / 100;

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode
        }).format(amount);
      } catch (e) {
        return '$' + amount.toFixed(2);
      }
    }

    updateTotal() {
      if (!this.items || this.items.length === 0) {
        this.items = Array.from(this.querySelectorAll('.bundle-item, .fbt-item'));
      }

      let calculatedTotal = 0;
      let baseTotal = 0;
      let allValid = true;

      const checkedCount = this.items.filter(node => node.classList.contains('checked')).length;
      this.discountActive = checkedCount >= 2;

      this.items.forEach(bundleItemNode => {
        // First update individual item price display to reflect discounts/variant
        this.updateItemPriceDisplay(bundleItemNode);

        const isChecked = bundleItemNode.classList.contains('checked');
        if (!isChecked) return;

        const { basePrice, effectivePrice, isValid } = this.getItemPrice(bundleItemNode);
        if (!isValid) {
          allValid = false;
        } else {
          calculatedTotal += effectivePrice;
          baseTotal += basePrice;
        }
      });

      this.total = calculatedTotal;

      if (this.totalElement) {
        this.totalElement.textContent = this.formatMoney(this.total);
      }

      const savings = baseTotal - calculatedTotal;

      // Compare-at price: the pre-discount total, struck through next to the total
      const compareEl = this.querySelector('.total-compare');
      if (compareEl) {
        if (savings > 0) {
          compareEl.textContent = this.formatMoney(baseTotal);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      // Show how much the discount saves on the selected items
      const savingsEl = this.querySelector('.bundle-savings');
      if (savingsEl) {
        const amountEl = savingsEl.querySelector('.savings-amount');
        if (savings > 0 && amountEl) {
          amountEl.textContent = this.formatMoney(savings);
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }

      if (this.submitButton) {
        const canSubmit = checkedCount > 0 && allValid;
        this.submitButton.disabled = !canSubmit;
        this.updateSubmitLabel(checkedCount);
      }
    }

    updateSubmitLabel(checkedCount) {
      if (!this.submitButton || this.isSubmitting) return;

      const selectLabel = this.getAttribute('data-label-select-items');
      const bundleLabel = this.getAttribute('data-label-add-bundle');
      const productsLabel = this.getAttribute('data-label-add-products');

      // No label attributes (e.g. FBT markup) — leave the button text alone
      if (!selectLabel && !bundleLabel && !productsLabel) return;

      let label;
      if (checkedCount === 0) {
        label = selectLabel;
      } else if (checkedCount === this.items.length && checkedCount >= 2) {
        label = bundleLabel;
      } else {
        label = productsLabel;
      }

      if (label) this.submitButton.textContent = label;
    }

    async initAutoPick() {
      const color = this.getAttribute('data-auto-pick-color');
      if (!color || this.autoPickStarted) return;
      this.autoPickStarted = true;

      const currentId = parseInt(this.getAttribute('data-current-product-id'), 10);
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const target = color.trim().toLowerCase();

      try {
        const searchUrl = rootPath + 'search/suggest.json?q=' + encodeURIComponent(color) +
          '&resources[type]=product&resources[limit]=10';
        const res = await fetch(searchUrl);
        if (!res.ok) { this.discard(); return; }

        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];

        const candidates = products.filter(p => {
          if (parseInt(p.id, 10) === currentId) return false;
          if (p.available === false) return false;
          const parts = p.title.split(' - ');
          return parts.length > 1 && parts[parts.length - 1].trim().toLowerCase() === target;
        });

        const list = this.querySelector('.bundle-list');
        if (!candidates.length || !list) { this.discard(); return; }

        // The stl-item view returns empty markup for ineligible products
        // (e.g. Accessories collection) — try candidates until one renders
        let html = '';
        for (const candidate of candidates) {
          const itemRes = await fetch(rootPath + 'products/' + candidate.handle + '?view=stl-item');
          if (!itemRes.ok) continue;
          const text = (await itemRes.text()).trim();
          if (text) { html = text; break; }
        }

        if (!html) { this.discard(); return; }

        list.insertAdjacentHTML('beforeend', html);

        this.items = Array.from(this.querySelectorAll('.bundle-item, .fbt-item'));
        this.setEvents();
        this.updateTotal();
        this.style.display = '';
        document.dispatchEvent(new CustomEvent('shop-the-look:visible'));
      } catch (err) {
        console.error('Shop The Look auto-pick error:', err);
        this.discard();
      }
    }

    // Remove the component (auto-pick found nothing) and let listeners know
    discard() {
      this.remove();
      document.dispatchEvent(new CustomEvent('shop-the-look:removed'));
    }

    handleSubmit(e) {
      if (e) e.preventDefault();

      const itemsToBuy = [];
      const discountCode = this.getAttribute('data-discount-code') || '';
      const regiosPropName = this.getAttribute('data-regios-property') || '_discount_code';

      this.items.forEach(bundleItemNode => {
        if (!bundleItemNode.classList.contains('checked')) return;

        const variantSelect = bundleItemNode.querySelector('.variant-selector, .fbt-variant-select');
        const variantId = variantSelect ? variantSelect.value : null;

        if (variantId) {
          itemsToBuy.push({
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: { '_shop_the_look': 'true' }
          });
        }
      });

      if (itemsToBuy.length > 0) {
        // The bundle discount only applies when buying more than one product
        const codeToApply = itemsToBuy.length >= 2 ? discountCode : '';
        if (codeToApply) {
          itemsToBuy.forEach(item => { item.properties[regiosPropName] = codeToApply; });
        }
        this.addItemAndApplyDiscount(itemsToBuy, codeToApply);
      }
    }

    async addItemAndApplyDiscount(itemsToBuy, discountCode) {
      if (!this.submitButton) return;

      const originalText = this.submitButton.textContent;
      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Adding...';

      try {
        const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        
        // Single POST request combining item additions, discount code, cart attribute, and cart-drawer section rendering
        const cartResponse = await fetch(rootPath + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: itemsToBuy,
            discount: discountCode || '',
            attributes: {
              'discount': discountCode || ''
            },
            sections: 'cart-drawer'
          })
        });

        if (!cartResponse.ok) {
          const errData = await cartResponse.json();
          throw new Error(errData.description || 'Failed to add items to cart');
        }

        const data = await cartResponse.json();

        // Update Cart Drawer UI directly using section HTML returned in single request
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl) {
          if (data.sections && data.sections['cart-drawer']) {
            cartDrawerEl.renderCartSectionHtml(data.sections['cart-drawer']);
          } else {
            await cartDrawerEl.refreshCart();
          }
          if (typeof cartDrawerEl.openCart === 'function') cartDrawerEl.openCart();
        }

        this.submitButton.textContent = 'Added!';

        // Notify the theme header and cart apps that the cart changed
        let cartData = null;
        try {
          const cartRes = await fetch(rootPath + 'cart.js', { headers: { 'Accept': 'application/json' } });
          if (cartRes.ok) cartData = await cartRes.json();
        } catch (cartErr) {
          console.warn('Shop The Look: could not fetch cart state', cartErr);
        }
        this.notifyCartApps(cartData);

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
      } catch (err) {
        console.error('Shop The Look add to cart error:', err);
        alert(err.message || 'Could not add items to bag.');
      } finally {
        setTimeout(() => {
          this.isSubmitting = false;
          this.submitButton.disabled = false;
          this.submitButton.textContent = originalText;
          this.updateTotal();
        }, 1500);
      }
    }

    notifyCartApps(cartData) {
      // Monster Cart Upsell app embed — re-renders its drawer from the cart
      if (typeof window.monster_refresh === 'function') {
        try { window.monster_refresh(); } catch (e) { console.warn('monster_refresh failed', e); }
      }

      // StyleHatch theme header: cart count and total
      if (cartData && window.StyleHatch && window.StyleHatch.AjaxCart &&
          typeof window.StyleHatch.AjaxCart.updateCartButton === 'function') {
        try { window.StyleHatch.AjaxCart.updateCartButton(cartData); } catch (e) { console.warn('updateCartButton failed', e); }
      }
    }

    async openCartDrawer(updatedCart) {
      const cartDrawerEl = document.querySelector('cart-drawer');
      if (cartDrawerEl) {
        if (typeof cartDrawerEl.refreshCart === 'function') {
          await cartDrawerEl.refreshCart();
        }
        if (typeof cartDrawerEl.openCart === 'function') {
          cartDrawerEl.openCart();
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: updatedCart } }));
    }
  }

  window.BundleComponent = BundleComponent;
  customElements.define('bundle-component', BundleComponent);
}